import styles from "./style.postcss";

import React from "react";
import { bindActionCreators } from "redux";
import { Link } from "react-router";
import { push } from "react-router-redux";
import is from "is_js";
import {
  connect,
  Icon,
  testClass,
  ApiResponseHelper,
} from "omni-common-ui";
import RcTooltip from "rc-tooltip";
import ProgressReportGenerationFailedDialog from "./ProgressReportGenerationFailedDialog";
import { isNoProgressTestResultsWithinIdpPeriodError } from "data/IndividualDevelopmentPlans/generateErrors";
import ReactGA from "react-ga4";
import UnsavedChangesTrackingPageComponent from "components/UnsavedChangesTrackingPageComponent";
import IndividualDevelopmentPlanStatus, {
  IdpStatus as IDP_STATUS,
} from "components/IndividualDevelopmentPlanStatus";
import CourseViewTable from "components/CourseViewTable";
import ExportAllHandler from "./ExportAllHandler";
import { actions as groupActions } from "containers/Group";
import {
  actions as individualDevelopmentPlans,
  accessors as idpAccessors,
} from "data/IndividualDevelopmentPlans";
import { actions as GroupMembers } from "data/GroupMembers";
import {
  actions as progressTestsPlanActions,
} from "data/ProgressTestsPlan";
import {
  actions as progressTestActions,
} from "data/ProgressTests";
import PropTypes from "prop-types";
import { Modes } from "./routes";
import { generateHfv4IndividualDevelopmentList } from "./actions";
import classnames from "classnames";
import { getHfv4BandForScore } from "../../UnitReportHFv4/StudentReportView/ScoreBand/hfv4ScoreBands";
import {
  buildAssessmentScoreMap,
  getAssessmentPlanFromState,
  getAssessmentProgressTestFromState,
} from "../assessmentScores";

function getScoreColorStyle(scoreStr) {
  if (!scoreStr || typeof scoreStr !== "string") return undefined;
  const match = scoreStr.match(/^(\d+)%$/);
  if (!match) return undefined;
  const band = getHfv4BandForScore(parseInt(match[1], 10));
  return band && band.color ? { color: band.color } : undefined;
}

const COLUMN_PERIOD = "period";
const COLUMN_STATUS = "status";
const COLUMN_ID = "id";

const SKILL_COLUMNS = [
  "Vocabulary",
  "Grammar",
  "Phonics",
  "Reading",
  "Listening",
  "Writing",
  "Speaking 1",
  "Speaking 2",
];

const GROUP_A_CODES = ["v4Bk0", "v4Bk1a", "v4Bk2a", "v4Bk3a", "v4Bk4a"];
const GROUP_B_CODES = ["v4Bk1b", "v4Bk2b", "v4Bk3b", "v4Bk4b"];

function getUnitRange(courseTypeLevelCode, reportName) {
  const isReport2 = reportName && /2/.test(reportName);
  if (GROUP_B_CODES.includes(courseTypeLevelCode)) {
    return isReport2 ? "7-12" : "7-9";
  }
  if (GROUP_A_CODES.includes(courseTypeLevelCode)) {
    return isReport2 ? "1-6" : "1-3";
  }
  return isReport2 ? "1-6" : "1-3";
}

class IndividualDevelopmentPlanListView extends UnsavedChangesTrackingPageComponent {
  constructor(props) {
    super(props);
    this.state = Object.assign({}, this.state, {
      showNoProgressTestResultsDialog: false,
    });
  }

  componentDidMount() {
    this._fetchAssessmentDataIfNeeded(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this._fetchAssessmentDataIfNeeded(nextProps);
  }

  _fetchAssessmentDataIfNeeded(props) {
    if (!props.isReport2) return;

    const { groupId } = props.params;
    props.fetchProgressTestPlanIfNeeded(groupId);

    if (props.assessmentPlan && ApiResponseHelper.shouldFetch(props.assessmentProgressTest)) {
      props.fetchProgressTest(groupId, props.assessmentPlan);
    }
  }

  routerWillLeave(nextLocation) {
    if (nextLocation && nextLocation.pathname.endsWith(Modes.GENERATE_ALL)) {
      const { groupIdpId, generateIndividualDevelopmentList } = this.props;
      if (groupIdpId) {
        generateIndividualDevelopmentList(groupIdpId)
          .then((error) => {
            if (isNoProgressTestResultsWithinIdpPeriodError(error)) {
              this.setState({ showNoProgressTestResultsDialog: true });
            }
          });
      }
      return false;
    }
  }

  _renderNoProgressTestResultsDialog() {
    if (!this.state.showNoProgressTestResultsDialog) {
      return null;
    }

    return (
      <ProgressReportGenerationFailedDialog
        isOpen
        onConfirmClick={() => {
          this.setState({ showNoProgressTestResultsDialog: false });
        }}
      />
    );
  }

  _gaLinkClicked() {
    ReactGA.event({
      category: "Individual Development Plan",
      action: "Clicked link to Individual Development Plan detail",
    });
  }

  _getStatus(studentIdp) {
    if (
      studentIdp.shouldShowSuggestions &&
      IDP_STATUS.INCOMPLETE === studentIdp.status.toLowerCase() &&
      SKILL_COLUMNS.every((skill) => !studentIdp[skill])
    ) {
      return "NA";
    }

    return studentIdp.status;
  }

  _renderSkillCell(suggestScore, assessmentScore) {
    const hasSuggestScore = suggestScore && suggestScore !== "–";
    const { isReport2, assessmentScoreMap } = this.props;
    const showAssessment = isReport2 && assessmentScoreMap;

    if (!hasSuggestScore && !assessmentScore) return "-";

    const unitRange = getUnitRange(
      this.props.courseTypeLevelCode,
      this.props.reportName,
    );

    if (showAssessment) {
      let triggerEl = null;
      const handlePopupAlign = (popupDomNode) => {
        const arrowEl = popupDomNode.querySelector('.rc-tooltip-arrow');
        if (!arrowEl || !triggerEl) return;
        const popupRect = popupDomNode.getBoundingClientRect();
        const triggerRect = triggerEl.getBoundingClientRect();
        const triggerCenterX = triggerRect.left + triggerRect.width / 2;
        let arrowLeft = triggerCenterX - popupRect.left;

        const minArrow = popupRect.width * 0.15;
        const maxArrow = popupRect.width * 0.85;
        if (arrowLeft > maxArrow) {
          const shift = arrowLeft - maxArrow;
          popupDomNode.style.left = `${(parseFloat(popupDomNode.style.left) || 0) + shift}px`;
          arrowLeft = maxArrow;
        } else if (arrowLeft < minArrow) {
          const shift = minArrow - arrowLeft;
          popupDomNode.style.left = `${(parseFloat(popupDomNode.style.left) || 0) - shift}px`;
          arrowLeft = minArrow;
        }
        arrowEl.style.left = `${arrowLeft}px`;
      };
      return (
        <RcTooltip
          placement="bottom"
          trigger={["hover"]}
          destroyPopupOnHide
          onPopupAlign={handlePopupAlign}
          overlay={
            <div>
              Average Score of Unit {unitRange}: <b>{suggestScore || '-'}</b> | Assessment Score: <b>{assessmentScore || '-'}</b>
            </div>
          }
          overlayClassName={classnames(styles.scoreTooltipBase, styles.splitScore_tooltip)}
        >
          <div ref={(el) => { triggerEl = el; }}>
            <span className={styles.splitScore}>
              <span style={getScoreColorStyle(suggestScore)}>{hasSuggestScore ? suggestScore : '-'}</span>
              <span className={styles.splitScore_divider}>|</span>
              <span className={styles.splitScore_assessment} style={getScoreColorStyle(assessmentScore)}>{assessmentScore || '-'}</span>
            </span>
          </div>
        </RcTooltip>
      );
    }

    if (!hasSuggestScore) return "-";

    return (
      <RcTooltip
        placement="bottom"
        trigger={["hover"]}
        destroyPopupOnHide
        overlay={
          <div>
            Average Score of Unit {unitRange}: <b>{suggestScore}</b>
          </div>
        }
        overlayClassName={styles.scoreTooltipBase}
      >
        <div>
          <span style={getScoreColorStyle(suggestScore)}>{suggestScore}</span>
        </div>
      </RcTooltip>
    );
  }

  _renderCell(member, column) {
    const idpList = this.props.individualDevelopmentPlanList;
    if (!idpList || !idpList.size) return "-";

    const studentIdp = idpList.find(
      (idp) => idp.studentId === member.studentGeneralInfo.id,
    );

    if (!studentIdp) return column !== COLUMN_ID && "-";
    switch (column) {
      case COLUMN_STATUS:
        return (
          <IndividualDevelopmentPlanStatus
            status={this._getStatus(studentIdp)}
          />
        );
      case COLUMN_ID:
        return (
          <Link
            className={classnames(
              styles.IndividualDevelopmentPlanListViewLink,
              testClass("report-link-cell"),
            )}
            to={this.props.buildRoute(`./student/${studentIdp.studentId}`)}
            draggable={false}
            onClick={() => this._gaLinkClicked()}
          >
            REPORT{" "}
            <Icon
              id="arrow-right"
              className={styles.IndividualDevelopmentPlanListViewLink_icon}
            />
          </Link>
        );
      default:
        if (SKILL_COLUMNS.includes(column)) {
          const assessmentScore = this.props.assessmentScoreMap
            && this.props.assessmentScoreMap[`${member.studentGeneralInfo.id}`]
            && this.props.assessmentScoreMap[`${member.studentGeneralInfo.id}`][column];
          return this._renderSkillCell(studentIdp[column], assessmentScore);
        }
        return studentIdp[column] || "-";
    }
  }

  _renderIdpList() {
    return (
      <CourseViewTable students={this.props.members} onlyRowDivider>
        <CourseViewTable.Header>
          <CourseViewTable.Row>
            <CourseViewTable.Cell columnId={COLUMN_PERIOD} small>
              Period covered
            </CourseViewTable.Cell>
            {SKILL_COLUMNS.map((skill) => (
              <CourseViewTable.Cell key={skill} columnId={skill} small>
                {skill}
              </CourseViewTable.Cell>
            ))}
            <CourseViewTable.Cell columnId={COLUMN_STATUS} small>
              Status
            </CourseViewTable.Cell>
            {this.props.individualDevelopmentPlanList &&
              this.props.individualDevelopmentPlanList.size > 0 && (
                <CourseViewTable.Cell columnId={COLUMN_ID} small />
              )}
          </CourseViewTable.Row>
        </CourseViewTable.Header>
        <CourseViewTable.Body>
          {(member, column) => this._renderCell(member, column)}
        </CourseViewTable.Body>
      </CourseViewTable>
    );
  }

  _renderExportDialog() {
    const {
      mode,
      buildRoute,
      pushRoute,
      languageCode,
      individualDevelopmentPlanList,
      groupIdpId,
      groupId,
      members,
      reportName,
    } = this.props;
    const isInExportAllRoute = mode === Modes.EXPORT_ALL;
    const memberStudentIds = members.map(
      (member) => member.studentGeneralInfo.id,
    );
    const exportableIDPList =
      individualDevelopmentPlanList &&
      individualDevelopmentPlanList.filter((idp) =>
        memberStudentIds.includes(idp.studentId),
      );
    return (
      individualDevelopmentPlanList &&
      individualDevelopmentPlanList.size > 0 &&
      isInExportAllRoute && (
        <ExportAllHandler
          individualDevelopmentPlanList={exportableIDPList}
          groupIdpId={groupIdpId}
          languageCode={languageCode}
          pushRoute={pushRoute}
          buildRoute={buildRoute}
          groupId={groupId}
          reportName={reportName}
        />
      )
    );
  }

  render() {
    return (
      <div>
        <div className={styles.IndividualDevelopmentPlanListView_content}>
          {this._renderIdpList()}
          {this._renderExportDialog()}
        </div>
        {this._renderNoProgressTestResultsDialog()}
      </div>
    );
  }
}

IndividualDevelopmentPlanListView.propTypes = {
  buildRoute: PropTypes.func.isRequired,
  pushRoute: PropTypes.func.isRequired,
  members: PropTypes.object.isRequired,
  groupIdpId: PropTypes.string.isRequired,
  individualDevelopmentPlanList: PropTypes.object.isRequired,
  mode: PropTypes.string.isRequired,
  reportName: PropTypes.string.isRequired,
  courseTypeLevelCode: PropTypes.string,
  isReport2: PropTypes.bool,
  assessmentScoreMap: PropTypes.object,
  assessmentPlan: PropTypes.object,
  assessmentProgressTest: PropTypes.object,
  fetchProgressTestPlanIfNeeded: PropTypes.func.isRequired,
  fetchProgressTest: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const { groupId, groupIdpId, mode } = ownProps.params;
  const { timeZone } = groupActions.getCentre(state, groupId);
  const group = state.get("rootReducer").get("groups").get(groupId).data;
  const languageCode = group.get("languageCode");

  const groupIdpList = state
    .get("rootReducer")
    .get("individualDevelopmentPlanList");
  const originIndividualDevelopmentPlanList = idpAccessors.getIdpList(
    groupIdpList,
    groupIdpId,
  );

  const individualDevelopmentPlanList = idpAccessors.formatToRenderUIBySkillName(
    originIndividualDevelopmentPlanList,
    timeZone,
  );
  const members = GroupMembers.getMembersByGroup(state, groupId);

  const reportName = idpAccessors.getReportName(state, groupIdpId);
  const courseTypeLevelCode = group.get("courseTypeLevelCode");

  const isReport2 = !!(reportName && /2/.test(reportName));
  let assessmentScoreMap = null;
  let assessmentPlan = null;
  let assessmentProgressTest = null;

  if (isReport2) {
    assessmentPlan = getAssessmentPlanFromState(state, groupId);
    assessmentProgressTest = getAssessmentProgressTestFromState(state, assessmentPlan);

    if (ApiResponseHelper.hasSucceeded(assessmentProgressTest)) {
      assessmentScoreMap = buildAssessmentScoreMap(
        originIndividualDevelopmentPlanList,
        assessmentProgressTest.data,
      );
    }
  }

  return {
    reportName,
    mode,
    state,
    members,
    individualDevelopmentPlanList,
    groupId,
    groupIdpId,
    languageCode,
    courseTypeLevelCode,
    isReport2,
    assessmentScoreMap,
    assessmentPlan,
    assessmentProgressTest,
  };
}

function mapDispatchToProps(dispatch) {
  const idpActions = bindActionCreators(individualDevelopmentPlans, dispatch);

  return Object.assign(
    {},
    { pushRoute: (path) => dispatch(push(path)) },
    idpActions,
    {
      generateIndividualDevelopmentList: (groupIdpId) =>
        dispatch(generateHfv4IndividualDevelopmentList(groupIdpId)),
    },
    bindActionCreators(progressTestsPlanActions, dispatch),
    bindActionCreators(progressTestActions, dispatch),
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(IndividualDevelopmentPlanListView);



// WEBPACK FOOTER //
// ./containers/Group/ProgressReportHFv4/IndividualDevelopmentPlanListView/component.jsx